import {
  Clock,
  Edit,
  Gauge,
  MapPin,
  Settings,
  Trash2,
  Wrench,
} from 'lucide-react'

import { formattedDateByLang } from 'src/helpers/formatDateByLang'
import { AlertMaintenanceUpdateWindow } from 'src/features/AlertMaintenanceUpdateWindow'
import { TooltipWrapper } from 'src/components/Tooltip'
import AlertDialogDemo from 'src/components/CustomAlertDialog'

import { useMaintenanceHistoryCard } from 'src/features/MaintenanceHistoryCard/useMaintenanceHistoryCard'
import { Card, CardContent } from 'src/components/shadcn/card'
import { FullMaintenanceProps } from 'src/api/Maintenance/types'

export const MaintenanceHistoryCard = ({
  date,
  dateOFLastEdit,
  serviceType,
  serviceCost,
  details,
  address,
  odometer,
  id,
  currency,
  distanceUnit,
}: FullMaintenanceProps) => {
  const {
    language,
    addMaintenanceLocale,
    locale,
    handleDelete,
    isEditMode,
    toggleEditMode,
    maintenanceDictionary,
  } = useMaintenanceHistoryCard()

  return (
    <Card className="group w-full p-4 border rounded-[10px] shadow-sm cursor-pointer transition transform hover:shadow-lg hover:-translate-y-1">
      <CardContent className="flex flex-col space-y-3 relative">
        <div className="absolute top-2 right-2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <AlertMaintenanceUpdateWindow
            itemId={id}
            isOpen={isEditMode}
            toggle={toggleEditMode}
            address={address}
            date={date}
            serviceType={serviceType}
            odometer={odometer}
            details={details}
            serviceCost={serviceCost}
            distanceUnit={distanceUnit}
            currency={currency}
          />
          <button
            className="text-gray-500 hover:text-blue-500"
            onClick={toggleEditMode}
          >
            <Edit size={16}></Edit>
          </button>
          <AlertDialogDemo
            trigger={
              <button className="text-gray-500 hover:text-red-500">
                <Trash2 size={16}></Trash2>
              </button>
            }
            onConfirm={() => handleDelete(id)}
            accept={locale.alert.accept}
            description={locale.alert.description}
            title={locale.alert.title}
            cancel={locale.alert.cancel}
          />
        </div>
        <div className="flex items-center space-x-2">
          <MapPin className="text-red-500" size={16} />
          <span className="text-lg font-semibold">{address}</span>
        </div>
        <TooltipWrapper
          text={
            dateOFLastEdit
              ? `${locale.dateOfLastEdit} ${formattedDateByLang(
                  dateOFLastEdit.seconds,
                  language,
                )}`
              : locale.noEdited
          }
        >
          <div className="flex items-center text-gray-500 text-sm">
            <Clock className="mr-1" size={16} />
            <span>{formattedDateByLang(date.seconds, language)}</span>
          </div>
        </TooltipWrapper>
        <div className="border-t pt-2">
          <div className="flex justify-between text-sm">
            <div className="flex items-center space-x-1">
              <Settings className="text-gray-500" size={16}></Settings>
              <span>{addMaintenanceLocale.serviceType}:</span>
            </div>
            <span>{maintenanceDictionary[serviceType]}</span>
          </div>
          <div className="flex justify-between text-sm">
            <div className="flex items-center space-x-1">
              <Gauge className="text-blue-500" size={16}></Gauge>
              <span>{addMaintenanceLocale.odometer}:</span>
            </div>
            <span>
              {odometer} ({distanceUnit})
            </span>
          </div>
          {details && (
            <div className="flex flex-col sm:flex-row justify-between items-start text-sm rounded-lg">
              <div className="flex items-center space-x-1 flex-shrink-0">
                <Wrench className="text-orange-500" size={16} />
                <span className="font-medium">
                  {addMaintenanceLocale.details}:
                </span>
              </div>
              <span className="text-start sm:text-end w-full sm:w-1/2 mt-1 sm:mt-0 my-1.5">
                {details}
              </span>
            </div>
          )}

          <div className="flex justify-between text-sm font-semibold mt-2">
            <span>{addMaintenanceLocale.cost}:</span>
            <span>
              {serviceCost} {currency.toUpperCase()}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
